'use client';

import type { INavListItem } from '@/api/types/public';
import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import {
  canDisplayResourceSlots,
  RESPONSIVE_RESOURCE_SLOT_SIZE,
  resolveVisibleResourceSlotCount,
} from './navigationLayoutPriority';
import ResourceSlotNav from './ResourceSlotNav.pc';
import styles from './index.module.scss';
import TextNav from './TextNav';
import { getTextNavSpmItemOffsets } from './submenuSpm';

interface TextResourceNavigationLayoutProps {
  textNavItems: INavListItem[];
  resourceSlotItems: INavListItem[];
  firstLevelIndex: number;
  firstLevelTitle: string;
  isDarkMode: boolean;
  isActive: boolean;
}

const RESPONSIVE_DESKTOP_QUERY = '(min-width: 1281px) and (max-width: 1919px)';
const WIDE_DESKTOP_QUERY = '(min-width: 1920px)';

const TextResourceNavigationLayout = ({
  textNavItems,
  resourceSlotItems,
  firstLevelIndex,
  firstLevelTitle,
  isDarkMode,
  isActive,
}: TextResourceNavigationLayoutProps) => {
  const layoutRef = useRef<HTMLDivElement>(null);
  const textNavigationRef = useRef<HTMLDivElement>(null);
  const [resourceSlotLayout, setResourceSlotLayout] = useState({
    visibleResourceSlotCount: resourceSlotItems.length,
    resourceSlotSize: RESPONSIVE_RESOURCE_SLOT_SIZE,
  });
  const textNavItemCount = textNavItems.length;
  const resourceSlotCount = resourceSlotItems.length;
  // 扁平坑位序号（submenu_item_GG_II 的 II）：文字区块按渲染顺序累计前置条目；
  // 资源位接在其后固定占 1 号（无跳转不赋码但占号）。
  const textNavSpmItemOffsets = getTextNavSpmItemOffsets(textNavItems);
  const resourceSlotSpmBase = textNavItems.reduce(
    (total, item) => total + (item.content?.list?.length || 0),
    0,
  );

  useLayoutEffect(() => {
    if (!isActive) return;

    const updateResourceSlotVisibility = () => {
      const layout = layoutRef.current;
      const textNavigation = textNavigationRef.current;
      if (!layout || !textNavigation) return;

      const isResponsiveDesktop = window.matchMedia(RESPONSIVE_DESKTOP_QUERY).matches;
      const isWideDesktop = window.matchMedia(WIDE_DESKTOP_QUERY).matches;
      const resourceSlotSize = Math.max(RESPONSIVE_RESOURCE_SLOT_SIZE, layout.clientHeight);
      const nextVisibleResourceSlotCount = isWideDesktop
        ? resolveVisibleResourceSlotCount({
            availableWidth: layout.clientWidth,
            higherPriorityWidth: textNavigation.scrollWidth,
            resourceSlotCount,
            resourceSlotSize,
          })
        : !isResponsiveDesktop ||
            canDisplayResourceSlots({
              availableWidth: layout.clientWidth,
              higherPriorityWidth: textNavigation.scrollWidth,
              resourceSlotCount,
              resourceSlotSize,
            })
          ? resourceSlotCount
          : 0;

      setResourceSlotLayout((currentLayout) =>
        currentLayout.visibleResourceSlotCount === nextVisibleResourceSlotCount &&
        currentLayout.resourceSlotSize === resourceSlotSize
          ? currentLayout
          : {
              visibleResourceSlotCount: nextVisibleResourceSlotCount,
              resourceSlotSize,
            },
      );
    };

    updateResourceSlotVisibility();

    const resizeObserver = new ResizeObserver(updateResourceSlotVisibility);
    if (layoutRef.current) resizeObserver.observe(layoutRef.current);
    if (textNavigationRef.current) resizeObserver.observe(textNavigationRef.current);

    return () => resizeObserver.disconnect();
  }, [isActive, resourceSlotCount, textNavItemCount]);

  const visibleResourceSlotItems = resourceSlotItems.slice(
    0,
    resourceSlotLayout.visibleResourceSlotCount,
  );

  return (
    <div ref={layoutRef} className={styles.textResourceNavigationLayout}>
      <div
        ref={textNavigationRef}
        className={`flex items-start ${styles.textResourceNavigationTextContainer}`}
      >
        {textNavItems.map((item, index) => (
          <div
            key={`${item.id}-${item.template_id}`}
            className={styles.responsiveTextNavigationColumn}
          >
            <TextNav
              item={item}
              firstLevelIndex={firstLevelIndex}
              firstLevelTitle={firstLevelTitle}
              secondLevelIndex={index}
              isLast={index === textNavItems.length - 1}
              isDarkMode={isDarkMode}
              isActive={isActive}
              spmItemOffset={textNavSpmItemOffsets.get(item) ?? 0}
            />
          </div>
        ))}
      </div>

      {visibleResourceSlotItems.length > 0 ? (
        <div
          className={`ml-auto flex shrink-0 ${styles.resourceSlotContainer} ${styles.textResourceNavigationResourceContainer}`}
          style={
            {
              '--resource-slot-size': `${resourceSlotLayout.resourceSlotSize}px`,
            } as CSSProperties
          }
        >
          {visibleResourceSlotItems.map((item, resourceIndex) => (
            <ResourceSlotNav
              key={`${item.id}-${item.template_id}`}
              item={item}
              firstLevelIndex={firstLevelIndex}
              firstLevelTitle={firstLevelTitle}
              resourceIndex={resourceIndex}
              spmItemOffset={resourceSlotSpmBase + resourceIndex}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default TextResourceNavigationLayout;
